import { Dispatch, SetStateAction, useMemo } from 'react';
import { css } from '@emotion/react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useLocationTimezone } from '@frontend/schedule-helpers';
import { theme } from '@frontend/theme';
import {
  Accordion,
  ChecklistMenuField,
  DateRangeField,
  FormValues,
  ModalControlModalProps,
  NakedButton,
  RadioField,
  Text,
  Tray,
  useForm,
} from '@frontend/design-system';
import { FilterableCheckboxList } from '../../../../components/filterable-checkbox-list';
import { useGetLocationOperatoryProviderList } from '../../../../hooks/scheduler-v3/use-get-location-operatory-provider-list';
import { getLabelWithTimezoneAbbr } from '../../../../utils';
import { CustomAccordionHeader } from '../../../appointment-list-filter-button/filter-drawer/custom-accordion-header';
import { AppliedFilters } from './applied-filters';

export interface FilterState {
  providers: string[];
  operatories: string[];
  serviceTypes: string[];
  startDate: string;
  endDate: string;
  view?: 'CURRENT' | 'EXPIRED';
  recurrenceTypes: ('SINGLE_OCCURRENCE' | 'WEEKLY' | 'SPECIFIC_DATE' | 'CUSTOM')[];
}

export type DetailedFiltersFormConfig = {
  providers: { type: 'checklist'; value: string[] };
  operatories: { type: 'checklist'; value: string[] };
  serviceTypes: { type: 'checklist'; value: string[] };
  dateRange: { type: 'dateRange'; value: [string, string] };
  view: { type: 'dropdown'; value: string };
  recurrenceType: { type: 'multiselect'; value: string[] };
};

export const DEFAULT_FILTERS: FilterState = {
  providers: [],
  operatories: [],
  serviceTypes: [],
  startDate: '',
  endDate: '',
  view: undefined,
  recurrenceTypes: [],
};

export const toFormValues = (filters: FilterState): FormValues<DetailedFiltersFormConfig> => ({
  providers: filters.providers,
  operatories: filters.operatories,
  serviceTypes: filters.serviceTypes,
  dateRange: [filters.startDate, filters.endDate],
  view: filters.view ?? 'CURRENT',
  recurrenceType: filters.recurrenceTypes ?? [],
});

const normalizeFilters = (values: FormValues<DetailedFiltersFormConfig>): FilterState => ({
  providers: values.providers || [],
  operatories: values.operatories || [],
  serviceTypes: values.serviceTypes || [],
  startDate: values.dateRange?.[0] || '',
  endDate: values.dateRange?.[1] || '',
  view: (values.view || undefined) as FilterState['view'],
  recurrenceTypes: (values.recurrenceType || []) as FilterState['recurrenceTypes'],
});

const hasActiveFilters = (filters: FilterState) => getActiveFilterCount(filters) > 0;

const getActiveFilterCount = (filters: FilterState) =>
  [
    filters.providers.length > 0,
    filters.operatories.length > 0,
    filters.serviceTypes.length > 0,
    !!filters.startDate,
    !!filters.endDate,
    !!filters.view && filters.view !== 'CURRENT',
    !!filters.recurrenceTypes?.length,
  ].filter(Boolean).length;

interface FiltersDrawerProps extends ModalControlModalProps {
  locationId: string;
  filters: FilterState;
  setFilters: Dispatch<SetStateAction<FilterState>>;
  setShowEnabledFiltersBadge?: (show: boolean) => void;
}

export const FiltersDrawer = ({
  locationId,
  filters,
  setFilters,
  setShowEnabledFiltersBadge,
  ...modalProps
}: FiltersDrawerProps) => {
  const { t } = useTranslation('schedule-settings');
  const { locationTimezoneAbbr } = useLocationTimezone(locationId);
  const { isV3OperatorySupported } = SchedulerV3.Helpers.useCheckV3OperatorySupport();

  const { providerList, operatoryList } = useGetLocationOperatoryProviderList({
    locationIds: locationId ? [locationId] : [],
  });

  const serviceTypesQuery = SchedulerV3.Helpers.useGetServiceTypesV3Data({
    selectedLocationId: locationId,
  });

  const serviceTypeOptions = useMemo(
    () =>
      (serviceTypesQuery.data?.appointmentTypeGroups || [])
        .flatMap((group) => group.bookableAppointmentTypes ?? [])
        .map((service) => ({ value: service.id ?? '', label: service.displayName || '' }))
        .filter((service) => service.value && service.label),
    [serviceTypesQuery.data?.appointmentTypeGroups]
  );

  const { getFieldProps, seedValues, values } = useForm<DetailedFiltersFormConfig, keyof DetailedFiltersFormConfig>({
    fields: {
      providers: { type: 'checklist', value: filters.providers },
      operatories: { type: 'checklist', value: filters.operatories },
      serviceTypes: { type: 'checklist', value: filters.serviceTypes },
      dateRange: { type: 'dateRange', value: [filters.startDate, filters.endDate] },
      view: { type: 'dropdown', value: filters.view || 'CURRENT' },
      recurrenceType: { type: 'multiselect', value: filters.recurrenceTypes ?? [] },
    },
  });

  const normalizedValues = normalizeFilters(values);

  const filterCounts = useMemo(() => {
    return {
      providers: normalizedValues.providers.length,
      operatories: normalizedValues.operatories.length,
      services: normalizedValues.serviceTypes.length,
    };
  }, [normalizedValues]);

  const filterLists = useMemo(() => {
    return {
      providers: providerList,
      operatories: operatoryList,
      services: serviceTypeOptions,
    };
  }, [providerList, operatoryList, serviceTypeOptions]);

  const handleReset = () => {
    seedValues(toFormValues(DEFAULT_FILTERS));
    setFilters(DEFAULT_FILTERS);
    setShowEnabledFiltersBadge?.(false);
  };

  const handleApply = () => {
    setFilters(normalizedValues);
    setShowEnabledFiltersBadge?.(hasActiveFilters(normalizedValues));
    modalProps.onClose?.();
  };

  const handleRemoveAppliedFilter = (type: string, value: string) => {
    switch (type) {
      case 'provider':
        getFieldProps('providers').onChange({ name: 'providers', value: [value] });
        break;
      case 'operatory':
        getFieldProps('operatories').onChange({ name: 'operatories', value: [value] });
        break;
      case 'service':
        getFieldProps('serviceTypes').onChange({ name: 'serviceTypes', value: [value] });
        break;
      case 'startDate': {
        const [, endDate] = values.dateRange || ['', ''];
        getFieldProps('dateRange').onChange({ name: 'dateRange', value: ['', endDate] });
        break;
      }
      case 'endDate': {
        const [startDate] = values.dateRange || ['', ''];
        getFieldProps('dateRange').onChange({ name: 'dateRange', value: [startDate, ''] });
        break;
      }
      case 'view':
        getFieldProps('view').onChange({ name: 'view', value: 'CURRENT' });
        break;
      case 'recurrence':
        getFieldProps('recurrenceType').onChange({ name: 'recurrenceType', value: '' });
        break;
    }
  };

  return (
    <Tray {...modalProps} width='smallXXL'>
      <Tray.Header
        Buttons={
          <NakedButton onClick={modalProps.onClose}>
            <Icon name='x' size={24} />
          </NakedButton>
        }
      >
        {t('Filters')}
      </Tray.Header>
      <Tray.Body
        css={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(2),
          padding: theme.spacing(0, 2, 0, 0),
          '& > section:last-of-type': {
            borderBottom: 'none',
          },
        }}
      >
        <FilterSectionWrapper title={t('Exception Target')}>
          <Accordion
            variant='main'
            shouldRenderBody
            chevronSize={16}
            css={{ '& > div:last-child': { marginBottom: 0 }, overflowY: 'auto' }}
          >
            <Accordion.Item value='providers'>
              <CustomAccordionHeader title={t('Providers')} icon='pending-small' filterCount={filterCounts.providers} />
              <Accordion.Body>
                <FilterableCheckboxList
                  listStyle={listStyle}
                  fieldName='providers'
                  checkListField={getFieldProps('providers')}
                  list={filterLists.providers}
                />
              </Accordion.Body>
            </Accordion.Item>
            {isV3OperatorySupported && (
              <Accordion.Item value='operatories'>
                <CustomAccordionHeader
                  title={t('Operatories')}
                  icon='tile-small'
                  filterCount={filterCounts.operatories}
                />
                <Accordion.Body>
                  <FilterableCheckboxList
                    listStyle={listStyle}
                    fieldName='operatories'
                    list={filterLists.operatories}
                    checkListField={getFieldProps('operatories')}
                  />
                </Accordion.Body>
              </Accordion.Item>
            )}
            <Accordion.Item value='services'>
              <CustomAccordionHeader title={t('Services')} icon='user-small' filterCount={filterCounts.services} />
              <Accordion.Body>
                <FilterableCheckboxList
                  listStyle={listStyle}
                  fieldName='serviceTypes'
                  list={filterLists.services}
                  checkListField={getFieldProps('serviceTypes')}
                />
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </FilterSectionWrapper>
        <FilterSectionWrapper title={t('Time Frame')}>
          <DateRangeField
            {...getFieldProps('dateRange')}
            label={t('Exception Date')}
            labelMin={getLabelWithTimezoneAbbr(t('Start Date'), locationTimezoneAbbr)}
            labelMax={getLabelWithTimezoneAbbr(t('End Date'), locationTimezoneAbbr)}
          />
        </FilterSectionWrapper>
        <FilterSectionWrapper title={t('View')}>
          <RadioField {...getFieldProps('view')}>
            <RadioField.Radio value='CURRENT'>{t('Current')}</RadioField.Radio>
            <RadioField.Radio value='EXPIRED'>{t('Expired')}</RadioField.Radio>
          </RadioField>
        </FilterSectionWrapper>
        <FilterSectionWrapper title={t('Recurrence')}>
          <ChecklistMenuField
            label={t('Recurrence')}
            {...getFieldProps('recurrenceType')}
            showSearchBar={false}
            showSelectionBar={false}
            floatingStyleOverrides={{ padding: theme.spacing(2, 0) }}
          >
            <ChecklistMenuField.Option value='SINGLE_OCCURRENCE'>{t('Single')}</ChecklistMenuField.Option>
            <ChecklistMenuField.Option value='WEEKLY'>{t('Weekly')}</ChecklistMenuField.Option>
            <ChecklistMenuField.Option value='CUSTOM'>{t('Custom')}</ChecklistMenuField.Option>
            <ChecklistMenuField.Option value='SPECIFIC_DATE'>{t('Specific Date')}</ChecklistMenuField.Option>
          </ChecklistMenuField>
        </FilterSectionWrapper>
      </Tray.Body>
      <AppliedFilters
        normalizedValues={normalizedValues}
        filterLists={filterLists}
        onRemoveFilter={handleRemoveAppliedFilter}
      />
      <Tray.Actions
        primaryLabel={t('Apply')}
        secondaryLabel={t('Reset')}
        onPrimaryClick={handleApply}
        onSecondaryClick={handleReset}
      />
    </Tray>
  );
};

const listStyle = css({
  minHeight: 200,
  maxHeight: 'calc(100vh - 645px)',
  overflowY: 'auto',
});

const FilterSectionWrapper = ({ title, children }: { title?: string; children: React.ReactNode }) => (
  <section
    css={{
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
      paddingBottom: theme.spacing(2),
      borderBottom: `1px solid ${theme.colors.neutral20}`,
    }}
  >
    {title && <Text weight='bold'>{title}</Text>}
    {children}
  </section>
);
